import { Component, h, Host, Prop, Event, Element, Watch } from '@stencil/core';
import { generateAnchorAttributes, openURL } from '../../utils/elements-interface';
export class Button {
  constructor() {
    /**
       Sets the button to a "loading" state, making it display a loading spinner.
       If the button has an icon, the spinner will replace the icon.
       If not, the spinner will replace the button's label.
      */
    this.loading = false;
    /**
     * HTML <a> target attribute
     */
    this.target = '_blank';
    /**
     * Sets button's type.
     */
    this.buttonType = 'button';
    /**
     * Sets button's content type
     */
    this.content = 'icon-left';
    /**
     * Sets button's size to small
     */
    this.small = false;
    /**
     * Sets button's state to disabled
     */
    this.disabled = false;
    /**
     * Sets button's state to active
     */
    this.checked = false;
    /**
     * Sets button's type
     */
    this.type = 'default';
    this.buttonRadioEvent = false;
    this.contentClasses = {
      'text-only': 'sy-btn-text-only',
      'icon-left': 'sy-btn-icon-left',
      'icon-right': 'sy-btn-icon-right',
      'icon-only': 'sy-btn-icon-only',
    };
    this.typesClasses = {
      primary: 'sy-btn-primary',
      accent: 'sy-btn-accent',
      error: 'sy-btn-error',
      success: 'sy-btn-success',
      default: '',
      basic: 'sy-btn-basic',
    };
    this.handleClick = (event) => {
      if (this.disabled) {
        event.stopImmediatePropagation();
      }
      else if (this.buttonType == 'check' || this.buttonType == 'radio') {
        this.checked = !this.checked;
      }
      else if (this.buttonType === 'button' && this.href) {
        openURL(this.href, event);
      }
      else {
        const form = this.el.closest('form');
        if (form) {
          event.preventDefault();
          if (this.buttonType === 'reset') {
            form.reset();
          }
          else if (this.buttonType === 'submit') {
            form.requestSubmit();
          }
        }
      }
    };
    this.handleButtonRadioToggle = (event) => {
      if (this.buttonType === 'radio' && this.name && event && event.target !== this.el) {
        const button = event.target;
        if (button && button.buttonType === 'radio' && button.name === this.name) {
          this.checked = false;
        }
      }
    };
  }
  async buttonTypeWatcher() {
    if (this.buttonType === 'radio') {
      this.addButtonRadioListener();
    }
    else {
      this.removeButtonRadioListener();
    }
  }
  async checkedWatcher() {
    if (this.buttonType == 'check' || this.buttonType == 'radio') {
      if (this.checked)
        this.syButtonDidCheck.emit();
      else
        this.syButtonDidUncheck.emit();
    }
  }
  async iconWatcher() {
    if (!this.icon)
      this.content = 'text-only';
  }
  getButtonAttributes() {
    const anchorAtributes = this.href && this.buttonType !== 'check' && this.buttonType !== 'radio' ? generateAnchorAttributes(this) : {};
    let attributes = Object.assign(Object.assign({}, anchorAtributes), { type: this.type, class: 'sy-btn ' + (this.small ? 'sy-btn-sm ' : '') + (this.checked ? 'active ' : '') + `${this.contentClasses[this.content]} ${this.typesClasses[this.type]}` });
    attributes = this.disabled ? Object.assign(Object.assign({}, attributes), { disabled: true }) : attributes;
    return attributes;
  }
  addButtonRadioListener() {
    if (this.buttonType === 'radio' && !this.buttonRadioEvent) {
      this.el.getRootNode().addEventListener('syButtonDidCheck', this.handleButtonRadioToggle);
      this.buttonRadioEvent = true;
    }
  }
  removeButtonRadioListener() {
    if (this.buttonRadioEvent) {
      this.el.getRootNode().removeEventListener('syButtonDidCheck', this.handleButtonRadioToggle);
      this.buttonRadioEvent = false;
    }
  }
  connectedCallback() {
    this.addButtonRadioListener();
  }
  componentWillLoad() {
    this.iconWatcher();
  }
  disconnectedCallback() {
    this.removeButtonRadioListener();
  }
  renderInnerButton() {
    return (h("div", { class: "sy-btn-inner-content" },
      !this.loading && this.icon && (this.content === 'icon-left' || this.content === 'icon-only') && h("sy-icon", { class: "sy-btn-icon", name: this.icon }),
      this.loading && this.content != 'icon-right' && this.renderLoadingSpinner(),
      !(this.loading && this.content === 'text-only') && this.content !== 'icon-only' && this.renderLabel(),
      !this.loading && this.icon && this.content === 'icon-right' && h("sy-icon", { class: "sy-btn-icon", name: this.icon }),
      this.loading && this.content === 'icon-right' && this.renderLoadingSpinner()));
  }
  renderLabel() {
    return this.label ? this.label : h("slot", null);
  }
  renderLoadingSpinner() {
    return h("sy-progress-spinner", { class: this.content, radius: 8, type: "indeterminate" });
  }
  render() {
    return (h(Host, { onClick: this.handleClick }, this.href && this.buttonType !== 'check' && this.buttonType !== 'radio' ? (h("a", Object.assign({}, this.getButtonAttributes()), this.renderInnerButton())) : (h("button", Object.assign({}, this.getButtonAttributes()),
      h("div", { class: "sy-btn-inner-content" }, this.renderInnerButton())))));
  }
  static get is() { return "sy-button"; }
  static get encapsulation() { return "shadow"; }
  static get delegatesFocus() { return true; }
  static get originalStyleUrls() { return {
    "$": ["scss/index.scss", "scss/_tokens.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["./scss/index.css", "./scss/_tokens.css"]
  }; }
  static get properties() { return {
    "loading": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the button to a \"loading\" state, making it display a loading spinner.\nIf the button has an icon, the spinner will replace the icon.\nIf not, the spinner will replace the button's label."
      },
      "attribute": "loading",
      "reflect": false,
      "defaultValue": "false"
    },
    "download": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string | undefined",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "HTML <a> download attribute"
      },
      "attribute": "download",
      "reflect": false
    },
    "href": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string | undefined",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "HTML <a> href attribute"
      },
      "attribute": "href",
      "reflect": false
    },
    "rel": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string | undefined",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "HTML <a> rel attribute"
      },
      "attribute": "rel",
      "reflect": false
    },
    "target": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'_blank' | '_self' | '_parent' | '_top'",
        "resolved": "\"_blank\" | \"_parent\" | \"_self\" | \"_top\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "HTML <a> target attribute"
      },
      "attribute": "target",
      "reflect": false,
      "defaultValue": "'_blank'"
    },
    "buttonType": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'button' | 'reset' | 'submit' | 'check' | 'radio'",
        "resolved": "\"button\" | \"check\" | \"radio\" | \"reset\" | \"submit\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's type."
      },
      "attribute": "button-type",
      "reflect": false,
      "defaultValue": "'button'"
    },
    "content": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "'text-only' | 'icon-left' | 'icon-only' | 'icon-right'",
        "resolved": "\"icon-left\" | \"icon-only\" | \"icon-right\" | \"text-only\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's content type"
      },
      "attribute": "content",
      "reflect": false,
      "defaultValue": "'icon-left'"
    },
    "name": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Optional button's name"
      },
      "attribute": "name",
      "reflect": false
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Optional button's label"
      },
      "attribute": "label",
      "reflect": false
    },
    "icon": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Icon name (use stardard lib)"
      },
      "attribute": "icon",
      "reflect": false
    },
    "small": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's size to small"
      },
      "attribute": "small",
      "reflect": true,
      "defaultValue": "false"
    },
    "disabled": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's state to disabled"
      },
      "attribute": "disabled",
      "reflect": true,
      "defaultValue": "false"
    },
    "checked": {
      "type": "boolean",
      "mutable": true,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's state to active"
      },
      "attribute": "checked",
      "reflect": true,
      "defaultValue": "false"
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "'primary' | 'accent' | 'error' | 'success' | 'default' | 'basic'",
        "resolved": "\"accent\" | \"basic\" | \"default\" | \"error\" | \"primary\" | \"success\"",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets button's type"
      },
      "attribute": "type",
      "reflect": true,
      "defaultValue": "'default'"
    }
  }; }
  static get events() { return [{
      "method": "syButtonDidCheck",
      "name": "syButtonDidCheck",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after the button is checked."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "syButtonDidUncheck",
      "name": "syButtonDidUncheck",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Emitted after the button is unchecked."
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }]; }
  static get elementRef() { return "el"; }
  static get watchers() { return [{
      "propName": "buttonType",
      "methodName": "buttonTypeWatcher"
    }, {
      "propName": "checked",
      "methodName": "checkedWatcher"
    }, {
      "propName": "icon",
      "methodName": "iconWatcher"
    }]; }
}
